import { Tooltip } from '@protoss/ui';
import * as React from 'react';
import { useDirtyStateContext } from '../../contexts/DirtyStateContext';
import { useLayout, type RightPaneMode } from '../../contexts/LayoutContext';
import type { RightRailItem } from './AppShell';

interface RightRailProps {
  items: RightRailItem[];
}

/**
 * RightRail - vertical icon navigation strip pinned to the far right edge
 *
 * Behavior (frontend-architecture.md §10, RightUtilityPane):
 * - each icon toggles a right pane menu:
 *     click inactive icon  → open pane, show that menu
 *     click active icon    → close pane (toggle off)
 *     click different icon → switch menu, keep pane open
 * - active icon has a clear selected state
 * - tooltips on the left side show the menu label
 * - no hamburger - switching modes changes content, not visibility
 */
export function RightRail({ items }: RightRailProps) {
  const { rightPaneVisible, rightPaneMode, setRightPaneVisible, setRightPaneMode } = useLayout();
  const { confirmIfDirty } = useDirtyStateContext();

  const handleClick = React.useCallback(
    (id: string) => {
      if (rightPaneVisible && rightPaneMode === id) {
        // Toggle off - close pane. Guard right-rail dirty sources.
        void confirmIfDirty('rr:').then((ok) => {
          if (!ok) return;
          setRightPaneVisible(false);
          setRightPaneMode(null);
        });
      } else if (rightPaneVisible) {
        // Switch mode - guard right-rail dirty sources before switching.
        void confirmIfDirty('rr:').then((ok) => {
          if (!ok) return;
          setRightPaneMode(id as RightPaneMode);
        });
      } else {
        // Open pane with this mode
        setRightPaneVisible(true);
        setRightPaneMode(id as RightPaneMode);
      }
    },
    [rightPaneVisible, rightPaneMode, setRightPaneVisible, setRightPaneMode, confirmIfDirty]
  );

  const visibleItems = items.filter((item) => item.enabled !== false);

  return (
    <div className="shell-rightrail" role="toolbar" aria-label="Right panel controls">
      {visibleItems.map((item) => {
        const isActive = rightPaneVisible && rightPaneMode === item.id;
        return (
          <Tooltip.Root key={item.id}>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className="shell-rightrail-btn"
                data-active={isActive ? 'true' : 'false'}
                data-editing={item.isEditing ? 'true' : 'false'}
                onClick={() => handleClick(item.id)}
                aria-label={item.isEditing ? `${item.label} (editing)` : item.label}
                aria-pressed={isActive}
              >
                <span className="shell-rightrail-btn-icon" aria-hidden="true">
                  {item.icon}
                </span>
                {item.isEditing ? (
                  <span className="shell-rightrail-btn-badge" aria-hidden="true" />
                ) : null}
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content side="left" sideOffset={8} className="ui-tooltip">
                {item.label}
                {item.isEditing ? ' · Editing' : ''}
                <Tooltip.Arrow className="ui-tooltip-arrow" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        );
      })}
    </div>
  );
}
